import { useState, useRef, useEffect } from 'react';
import { loginPath, prepareLogout } from '../../utils/loginSurface';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import {
  Menu,
  Search,
  Bell,
  ChevronDown,
  LogOut,
  Settings,
  Lock,
  User,
  Sparkles,
} from 'lucide-react';
import { useAppStore } from '../../stores/appStore';
import { useAuthStore } from '../../stores/authStore';
import { useAiChatStore } from '../../stores/aiChatStore';
import { toast, confirm } from '../../stores/uiStore';
import { openNotification } from '../../utils/notificationLink';
import { companyBadge } from '../../utils/formatters';
import PendingApprovalsButton from './PendingApprovalsButton';
import { useChatDock } from '../../stores/chatDockStore';
import { searchablePages } from './navStructure';
import { NotificationBellButton } from '../notifications/NotificationPanel';


/**
 * Ortam rozeti — YALNIZ dev/test ortamında görünür, PROD'da hiçbir şey basmaz.
 *
 * İki ortamın ekranı birebir aynı olduğu için dev sekmesine bakıp "yeni tekliflerim
 * görünmüyor" sanmak mümkündü (07.08 vakası: prod'da 13.900, dev'de 13.788 teklif,
 * ayrı veritabanları). Ortam bilgisi /health'ten gelir (ReportDb adına göre).
 */
function EnvBadge() {
  const { data } = useQuery({
    queryKey: ['app-env'],
    queryFn: async () => (await api.get('/health')).data,
    staleTime: 60 * 60 * 1000,   // ortam oturum boyunca değişmez
    retry: false,
  })
  const env = data?.env
  // YALNIZ açıkça 'development' iken göster. Eskiden "production değilse göster" idi →
  // /health env'i 'unknown' dönerse (ör. _is_prod hata verirse) CANLI sistemde "TEST"
  // rozeti çıkardı. Belirsizlikte rozet BASILMAZ (yanlış alarm canlıda daha kötü).
  if (env !== 'development') return null
  return (
    <span
      className="text-[10px] font-bold leading-none px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300 tracking-wide"
      title="TEST ortamı — buradaki veriler canlıdan AYRI bir veritabanındadır. Canlı sistem için :5050 adresini kullan."
    >
      TEST
    </span>
  )
}

function AiChatButton() {
  const toggleChat = useAiChatStore((s) => s.toggleChat)
  return (
    <button
      onClick={toggleChat}
      title="VERTEX AI Asistan"
      className="relative p-2 rounded-lg bg-gradient-to-br from-primary-500 to-violet-600 text-white hover:shadow-lg hover:scale-105 transition-all"
    >
      <Sparkles size={18} />
    </button>
  )
}

function ClockDisplay() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  // Europe/Istanbul tz
  const time = new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(now)
  const date = new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(now)
  return (
    <div
      title={`Sistem saati (Europe/Istanbul, UTC+03:00) · ${date} ${time}`}
      className="hidden md:flex flex-col items-end leading-tight px-2 py-1 rounded-lg hover:bg-surface-100"
    >
      <span className="text-sm font-semibold text-text-primary tabular-nums">{time}</span>
      <span className="text-[10px] text-text-secondary tabular-nums">{date} · GMT+3 İstanbul</span>
    </div>
  )
}

// Popup gösterilmiş bildirim id'leri — sayfa yenilense de tekrar açılmasın
const POPPED_KEY = 'vertex_popped_notifications'
const readPopped = () => {
  try { return new Set(JSON.parse(localStorage.getItem(POPPED_KEY) || '[]')) } catch { return new Set() }
}
const writePopped = (set) => {
  try { localStorage.setItem(POPPED_KEY, JSON.stringify([...set].slice(-300))) } catch { /* kota dolu */ }
}

// İş ortağı portalı: eski zil (tık = Bildirimler sayfası, gelen bildirim kalıcı açılır pencere).
// İç kullanıcıda zil sağ Bildirim panelini açar/kapatır (components/notifications/NotificationPanel).
function PartnerNotificationBell() {
  const navigate = useNavigate()
  const setActiveCompany = useAppStore((st) => st.setActiveCompany)
  const { data } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => {
      const r = await api.get('/notifications/', {
        params: { limit: 10, unread_only: true },
        timeout: 10000,
        silentNetToast: true,
      })
      return r.data?.data || { items: [], total: 0 }
    },
    refetchInterval: 60_000,
  })

  // ── GELEN BİLDİRİM → KALICI POPUP ──
  // Kendiliğinden kapanmaz; kullanıcı çarpıya basana kadar ekranda kalır. Gövdeye tıklayınca
  // bildirimin sayfasına gider. İlk yüklemede MEVCUT okunmamışlar patlatılmaz (yalnız
  // "seen" işaretlenir) — aksi halde 20 okunmamışta 20 popup açılırdı.
  const openChatChannel = useChatDock((st) => st.openChannel)
  const primedRef = useRef(false)
  useEffect(() => {
    const items = data?.items || []
    const seen = readPopped()
    if (!primedRef.current) {
      primedRef.current = true                      // ilk tur: sessizce işaretle
      items.forEach((n) => n?.id != null && seen.add(String(n.id)))
      writePopped(seen)
      return
    }
    const fresh = items.filter((n) => n?.id != null && !seen.has(String(n.id)))
    if (!fresh.length) return
    // Sohbet AÇIKKEN sohbet mesaj bildirimleri (chat.*) için popup ÇIKMAZ — mesajın
    // kendisi zaten balon olarak akışa düşüyor (WhatsApp/Slack davranışı). Popup, mesaj
    // balonundan önce belirip "bildirim geldi ama sohbette yok" hissi veriyordu. Diğer
    // bildirimler (onay, izin, masraf…) sohbette de çıkmaya devam eder.
    let inChat = false
    try { inChat = !!useChatDock.getState().open } catch { /* store yok */ }
    if (!inChat) { try { inChat = String(window.location?.pathname || '').startsWith('/chat') } catch { /* yut */ } }
    // Bastırılanlar da "görüldü" işaretlenir (aşağıda) → sohbetten çıkınca geç popup patlamaz.
    const poppable = inChat ? fresh.filter((n) => !String(n.type || '').startsWith('chat.')) : fresh
    poppable.slice(0, 3).forEach((n) => {           // aynı anda en fazla 3 popup
      toast.persistent(n.title || 'Yeni bildirim', n.body || n.message || '', {
        type: 'info',
        // Hangi firmadan geldiği rozetle görünsün: kullanıcı iki firmada da
        // çalışıyor ve bildirimin kaynağı metinden anlaşılmıyordu.
        badge: companyBadge(n.company),
        // Başka firmadan gelen bildirimde ÖNCE aktif firmayı çevir, sonra git — aksi halde
        // (ör. Adeptek'teyken Forte bildirimi) hedef sayfa boş açılıyordu.
        onClick: () => openNotification(n, navigate, setActiveCompany, openChatChannel),
      })
    })
    fresh.forEach((n) => seen.add(String(n.id)))
    writePopped(seen)
  }, [data, navigate, setActiveCompany])

  const unread = data?.unread_count ?? data?.total ?? 0
  return (
    <button
      onClick={() => navigate('/notifications')}
      title="Bildirimler"
      className="relative p-2 hover:bg-surface-100 rounded-lg transition-colors"
    >
      <Bell size={20} className="text-text-secondary" />
      {unread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </button>
  )
}

function CompanySwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const { activeCompany, setActiveCompany } = useAppStore();
  const user = useAuthStore((s) => s.user);
  const userRole = user?.role;
  const allowedCompanies = user?.allowed_companies || [];

  const allCompanies = [
    { value: '01', label: 'Adeptek' },
    { value: '02', label: 'Forte' },
    { value: '*', label: 'Tümü' },
  ];

  // Sadece kullanıcının erişebildiği firmaları göster
  // "Tümü" seçeneği: sadece birden fazla firma yetkisi varsa ve GM/ADMIN ise
  const isGmOrAdmin = userRole === 'ADMIN' || userRole === 'GM';
  const companies = allCompanies.filter(c => {
    if (c.value === '*') return allowedCompanies.length > 1 && isGmOrAdmin;
    return allowedCompanies.includes(c.value);
  });

  // Switcher'ı ROL değil YETKİ belirler: birden fazla firmaya yetkili olan
  // değiştirebilir. Önceden SALES rolü koşulsuz gizleniyordu; yöneticinin
  // ilgili kullanıcıya verdiği ikinci firma yetkisi böylece etkisiz kalıyordu.
  // Tek firmalı kullanıcıda (satışçıların çoğu) liste zaten tek elemanlı olduğu
  // için switcher yine görünmez. Firma seçimi sunucuda da doğrulanır
  // (validate_company → allowed_companies), yani gizleme bir güvenlik katmanı değil.
  if (companies.length <= 1) return null;

  const selectedCompany =
    companies.find((c) => c.value === activeCompany)?.label || 'Seç';

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-surface-100 rounded-lg transition-colors"
      >
        <span>{selectedCompany}</span>
        <ChevronDown size={16} />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-1 w-40 bg-surface-0 border border-surface-200 rounded-lg shadow-lg z-50">
          {companies.map((company) => (
            <button
              key={company.value}
              onClick={() => {
                setActiveCompany(company.value);
                setIsOpen(false);
              }}
              className={`
                block w-full text-left px-4 py-2 text-sm transition-colors
                ${
                  activeCompany === company.value
                    ? 'bg-primary-50 text-primary-600 font-medium'
                    : 'text-text-secondary hover:bg-surface-100'
                }
              `}
            >
              {company.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu({ partner = false, onChangePassword }) {
  const [isOpen, setIsOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const displayName = user?.display_name || user?.username || 'Kullanıcı';
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    setIsOpen(false);
    const ok = await confirm({
      title: 'Çıkış yapılsın mı?',
      message: 'Oturumunuz kapatılacak.',
      confirmLabel: 'Çıkış yap',
      cancelLabel: 'Vazgeç',
    });
    if (!ok) return;
    // Kapı hesabın türünden: iş ortağı /partner/login'e, iç kullanıcı /login'e döner.
    prepareLogout(user);
    logout();
    navigate(loginPath(), { replace: true });
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 hover:bg-surface-100 rounded-lg transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-brand-purple text-white flex items-center justify-center text-xs font-semibold">
          {initials}
        </div>
        <span className="text-sm font-medium text-text-primary hidden sm:block">
          {displayName}
        </span>
        <ChevronDown size={16} className="text-text-muted" />
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-surface-0 border border-surface-200 rounded-lg shadow-lg z-50">
          <button
            onClick={() => {
              navigate('/settings/profile');
              setIsOpen(false);
            }}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-text-secondary hover:bg-surface-100 transition-colors border-b border-surface-100"
          >
            <User size={16} />
            Profil
          </button>
          <button
            onClick={() => {
              // İş ortağında şifre açılır pencerede değişir (sayfa yok)
              if (onChangePassword) onChangePassword(); else navigate('/settings/security');
              setIsOpen(false);
            }}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-text-secondary hover:bg-surface-100 transition-colors border-b border-surface-100"
          >
            <Lock size={16} />
            Şifreyi Değiştir
          </button>
          <button
            onClick={() => {
              // İş ortağı /settings kabuğuna giremez (partnerPathAllowed) → bildirim tercihleri
              navigate(partner ? '/settings/notifications' : '/settings');
              setIsOpen(false);
            }}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-text-secondary hover:bg-surface-100 transition-colors border-b border-surface-100"
          >
            <Settings size={16} />
            {partner ? 'Bildirim ayarları' : 'Ayarlar'}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-accent-red hover:bg-red-50 transition-colors"
          >
            <LogOut size={16} />
            Çıkış Yap
          </button>
        </div>
      )}
    </div>
  );
}

/** Menü ağacından türetilen, yetkiye göre süzülmüş sayfa listesi (elle liste bayatlıyordu). */
function useNavSearchPages() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const userRole = useAuthStore((s) => s.user?.role);
  const partnerIsAdmin = useAuthStore((s) => !!s.user?.partner_is_admin);
  // memo YOK: hasPermission'ın kimliği yetki tazelenince değişmeyebilir; ~200 öğe ucuz
  return searchablePages({ hasPermission, userRole, partnerIsAdmin });
}

function PageSearch({ pages }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);

  // Türkçe harf duyarsız; grup adıyla da bulunur ("finans borç" → Finans › Borçlar)
  const results = (() => {
    const q = query.trim().toLocaleLowerCase('tr');
    if (!q) return [];
    const words = q.split(/\s+/);
    return pages.filter((p) => {
      const hay = p.search || p.label.toLocaleLowerCase('tr');
      return words.every((w) => hay.includes(w));
    }).slice(0, 10);
  })();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleChange = (e) => {
    setQuery(e.target.value);
    setIsOpen(true);
    setActiveIndex(0);
  };

  const goToPage = (path) => {
    navigate(path);
    setQuery('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIndex]) goToPage(results[activeIndex].path);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query.trim().length > 0 && setIsOpen(true)}
        placeholder="Sayfa ara..."
        className="w-full pl-10 pr-4 py-2 text-sm border border-surface-200 rounded-lg bg-surface-50 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
      />
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-surface-0 border border-surface-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {results.map((page, index) => (
            <button
              key={page.path}
              onMouseDown={() => goToPage(page.path)}
              className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left transition-colors ${
                index === activeIndex
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-text-secondary hover:bg-surface-100'
              }`}
            >
              <Search size={14} className="shrink-0 text-text-muted" />
              <span className="truncate">{page.label}</span>
              {page.group && <span className="ml-auto pl-2 shrink-0 text-xs text-text-muted">{page.group}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * `variant="partner"`: iş ortağı portalının üst çubuğu (kullanıcı isteği 16.10.2026 — "aynen bizde
 * olduğu gibi"). Sayfa araması yalnız partner sayfalarını arar; yapay zekâ asistanı (iç veriye
 * bağlı) ve onay rozeti (partnerin modülü değil) GÖSTERİLMEZ; firma seçici tek firmalıda zaten gizli.
 */
export default function Header({ pageTitle, variant = 'internal', searchPages, onMenu, onChangePassword }) {
  const isPartnerBar = variant === 'partner';
  const navPages = useNavSearchPages();
  const navigate = useNavigate();
  const { setIsSidebarExpanded, setMobileSidebarOpen, mobileSidebarOpen } =
    useAppStore();

  return (
    /* Katman bandı — .claude/rules/frontend.md "Katman (z-index) düzeni":
       içerik 0-30 · KABUK 35-40 · örtü/modal 40+ · portal açılır 110-130.
       ⚠️ z-20 iken header'ın KENDİ açılır menüleri (firma seçici, kullanıcı menüsü,
       sayfa arama) z-50 olmalarına rağmen sayfa içeriğinin ALTINDA kalıyordu: menü
       header'ın yığın bağlamı (z-20) içinde yaşar, dışarıdaki z-30 öğeler onu örter. */
    <header className="sticky top-0 z-[35] bg-surface-0">
      <div className="app-header-bar flex items-center justify-between h-16 px-4 lg:px-6 gap-4">
        {/* Left: Hamburger (sadece mobile) */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button
            onClick={() => (onMenu ? onMenu() : setMobileSidebarOpen(!mobileSidebarOpen))}
            className="lg:hidden p-2 hover:bg-surface-100 rounded-lg transition-colors"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Center: Search (hidden on small screens) */}
        <div className="hidden md:flex flex-1 max-w-md">
          <PageSearch pages={searchPages || navPages} />
        </div>

        {/* Right: Company Switcher + AI + Notifications + User Menu */}
        <div className="flex items-center gap-2 lg:gap-4">
          {/* ORTAM ROZETİ — DEV ve PROD ekranları birebir aynı görünüyordu; dev sekmesine
              bakıp "yeni tekliflerim yok" sanmak mümkündü. PROD'da hiçbir şey gösterilmez. */}
          <EnvBadge />
          {/* Build versiyonu — her deploy'da değişir (deploy doğrulama). İş ortağına gösterilmez. */}
          {!isPartnerBar && <span
            className="hidden lg:inline text-[10px] leading-none text-text-muted font-mono px-2 py-1 rounded bg-surface-100"
            title="Build versiyonu (git hash · tarih). Her deploy'da değişir."
          >
            v{typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'}
          </span>}
          {!isPartnerBar && <CompanySwitcher />}

          {/* AI Chatbot — iç veriye bağlı, iş ortağına açılmaz */}
          {!isPartnerBar && <AiChatButton />}

          {/* Sistem Saati (Europe/Istanbul) */}
          <ClockDisplay />

          {/* Bekleyen Onaylar — teklif + izin/görev + masraf tek rozette */}
          {!isPartnerBar && <PendingApprovalsButton />}

          {/* Notifications */}
          {isPartnerBar ? <PartnerNotificationBell /> : <NotificationBellButton />}

          {/* Ayarlar — menü şeridinde değil, burada (HubSpot düzeni) */}
          {!isPartnerBar && (
            <button
              type="button"
              onClick={() => navigate('/settings')}
              title="Ayarlar"
              className="p-2 rounded-lg text-text-secondary hover:bg-surface-100 hover:text-text-primary transition-colors"
            >
              <Settings size={20} />
            </button>
          )}


          {/* User Menu */}
          <UserMenu partner={isPartnerBar} onChangePassword={isPartnerBar ? onChangePassword : undefined} />
        </div>
      </div>
    </header>
  );
}
